import { Controller } from "@hotwired/stimulus";
import { showConfirmModal } from "../lib/custom_confirm";

const CARD_DIRTY = "card-dirty";
const LEAVE_TITLE = "Unsaved changes";
const LEAVE_MESSAGE = "You have changes you haven't saved. Leave this page and lose them?";
const LEAVE_LABEL = "Discard & Leave";

// Asks before leaving a page that holds unsaved edits.
//
// Optional values:
//   dirtyFlag   — the camelCase dataset key of a flag ANOTHER controller keeps
//                 on this element ("true" while it holds unsaved edits). When
//                 set, that flag is the answer: the Trust Center wizard keeps
//                 data-trust-center-wizard-dirty per step and clears it on each
//                 Continue (#18683), which a bare "something changed" flag
//                 would get wrong.
//   guardVisits — also ask before an in-app Turbo visit (a sidebar link), not
//                 only before a full unload. Uses the styled confirm modal,
//                 never window.confirm.
export default class extends Controller {
  static targets = ["form", "saveBar"];

  static values = {
    dirtyFlag: String,
    guardVisits: Boolean,
  };

  connect() {
    this.isDirty = false;
    this.isSubmitting = false;
    this.leaving = false;
    this.boundBeforeUnload = this.beforeUnload.bind(this);
    this.boundBeforeVisit = this.beforeVisit.bind(this);
    window.addEventListener("beforeunload", this.boundBeforeUnload);
    if (this.guardVisitsValue) document.addEventListener("turbo:before-visit", this.boundBeforeVisit);
    this.hideSaveBar();
  }

  disconnect() {
    window.removeEventListener("beforeunload", this.boundBeforeUnload);
    document.removeEventListener("turbo:before-visit", this.boundBeforeVisit);
  }

  trackChange() {
    this.isDirty = true;
    this.showSaveBar();
  }

  formSubmitted(event) {
    if (event?.defaultPrevented) return;

    this.isSubmitting = true;
  }

  hideSaveBar() {
    if (this.hasSaveBarTarget) {
      this.saveBarTarget.classList.add("d-none");
    }
  }

  showSaveBar() {
    if (this.hasSaveBarTarget) {
      this.saveBarTarget.classList.remove("d-none");
    }
  }

  // Whether leaving would lose an edit. A dirtyFlag owner answers first. A
  // form that also carries card-dirty (every Settings / profile card) answers
  // with card-dirty's comparison against what was loaded, so an edit that was
  // typed and then undone does not prompt (#18256, BUTTON-9) — one
  // comparison, never a second copy. A form without either keeps the
  // "something was changed" flag.
  hasUnsavedChanges() {
    if (this.hasDirtyFlagValue && this.dirtyFlagValue) {
      return this.element.dataset[this.dirtyFlagValue] === "true";
    }
    const cardDirty = this.cardDirtyController();
    return cardDirty ? cardDirty.dirty : this.isDirty;
  }

  cardDirtyController() {
    const form = this.hasFormTarget ? this.formTarget : this.element;
    return (
      this.application.getControllerForElementAndIdentifier(form, CARD_DIRTY) ||
      this.application.getControllerForElementAndIdentifier(this.element, CARD_DIRTY)
    );
  }

  beforeUnload(event) {
    if (this.leaving) return;
    if (this.hasUnsavedChanges() && !this.isSubmitting) {
      event.preventDefault();
      event.returnValue = "";
    }
  }

  // turbo:before-visit is cancelable; stop the visit, ask, and re-issue it
  // once the user accepts. One prompt at a time.
  beforeVisit(event) {
    if (this.leaving || this.isSubmitting || !this.hasUnsavedChanges()) return;

    event.preventDefault();
    if (this.prompting) return;
    this.prompting = true;
    const url = event.detail?.url;

    showConfirmModal(LEAVE_MESSAGE, { variant: "warning", title: LEAVE_TITLE, confirmText: LEAVE_LABEL }).then(
      (confirmed) => {
        this.prompting = false;
        if (!confirmed || !url) return;

        this.leaving = true;
        if (window.Turbo) window.Turbo.visit(url);
        else window.location.assign(url);
      }
    );
  }
}
